// Analytics utility functions for Authentipet website
// Supports both Google Analytics 4 and Google Tag Manager

/**
 * Retrieves the Google Analytics Measurement ID.
 * Checks window.GA_MEASUREMENT_ID first, then Vite env var, then falls back to default.
 * @returns {string} The GA Measurement ID.
 */
const getGAId = () => {
  try {
    if (typeof window !== 'undefined' && window.GA_MEASUREMENT_ID) {
      return window.GA_MEASUREMENT_ID
    }
    if (import.meta.env.VITE_GA_MEASUREMENT_ID) {
      return import.meta.env.VITE_GA_MEASUREMENT_ID
    }
  } catch (e) {
    console.warn('Error retrieving GA ID:', e)
  }
  return 'G-WZW8C2S0KQ'
}

const GA_ID = getGAId()

/**
 * Checks if analytics (gtag) is available on the window object.
 * @returns {boolean} True if gtag is available.
 */
const isAnalyticsAvailable = () => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// --- Week 3 minimal GA4 events (snake_case) ---

export const tapCta = (cta_id, page_path = (typeof window !== 'undefined' ? window.location.pathname : undefined), position) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'tap_cta', { cta_id, page_path, position });
  }
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: 'tap_cta', cta_id, page_path, position });
  }
};

export const formStart = (form_id) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'form_start', { form_id });
  }
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: 'form_start', form_id });
  }
};

export const formStepView = (form_id, step_index) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'form_step_view', { form_id, step_index });
  }
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: 'form_step_view', form_id, step_index });
  }
};

export const formSubmit = (form_id, successful, duration_ms) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'form_submit', { form_id, successful, duration_ms });
  }
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: 'form_submit', form_id, successful, duration_ms });
  }
};

// Track page views
export const trackPageView = (pagePath, pageTitle) => {
  if (isAnalyticsAvailable()) {
    window.gtag('config', GA_ID, {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

// Track errors
export const trackError = (errorType, error) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'error', {
      event_category: 'Error',
      event_label: errorType,
      error_message: error?.message || String(error)
    });
  }
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'error',
      error_type: errorType,
      error_message: error?.message || String(error)
    });
  }
};

// Track conversions
export const trackConversion = (conversionType, value = null, currency = 'USD') => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'conversion', {
      event_category: 'Conversion',
      event_label: conversionType,
      value: value,
      currency: currency
    });
  }
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'conversion',
      conversion_type: conversionType,
      conversion_value: value,
      conversion_currency: currency
    });
  }
};

// Track form interactions
export const trackFormEvent = (formName, action, value = null) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', action, {
      event_category: 'Form Interaction',
      event_label: formName,
      value: value,
      custom_parameter_2: 'form_interaction'
    });
  }

  // Also push to dataLayer for GTM
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'form_interaction',
      form_name: formName,
      form_action: action,
      form_value: value
    });
  }
};

// Track calculator usage
export const trackCalculatorEvent = (action, competitorRate = null, savings = null) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'calculator_interaction', {
      event_category: 'Price Calculator',
      event_label: action,
      competitor_rate: competitorRate,
      calculated_savings: savings,
      custom_parameter_1: 'calculator_user'
    });
  }

  // Enhanced ecommerce tracking for calculator as "product interaction"
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'calculator_used',
      calculator_action: action,
      competitor_rate: competitorRate,
      savings_amount: savings,
      engagement_score: savings > 500 ? 'high_value' : 'standard'
    });
  }
};

// Track button clicks and CTA interactions
export const trackCTAClick = (ctaText, ctaLocation, destination = null) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'cta_click', {
      event_category: 'CTA Interaction',
      event_label: ctaText,
      cta_location: ctaLocation,
      destination: destination
    });
  }

  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'cta_interaction',
      cta_text: ctaText,
      cta_location: ctaLocation,
      click_destination: destination
    });
  }
};

// Track pricing page interactions
export const trackPricingEvent = (action, planType = null) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'pricing_interaction', {
      event_category: 'Pricing',
      event_label: action,
      plan_type: planType
    });
  }

  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'pricing_engagement',
      pricing_action: action,
      selected_plan: planType
    });
  }
};

// Track video or media interactions
export const trackMediaEvent = (mediaType, action, mediaName) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', action, {
      event_category: `${mediaType} Interaction`,
      event_label: mediaName
    });
  }
};

// Track user engagement milestones
export const trackEngagementMilestone = (milestone, timeOnPage = null) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'engagement_milestone', {
      event_category: 'User Engagement',
      event_label: milestone,
      value: timeOnPage
    });
  }

  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'engagement_milestone',
      milestone_type: milestone,
      time_on_page: timeOnPage,
      engagement_level: timeOnPage > 120 ? 'high' : timeOnPage > 60 ? 'medium' : 'low'
    });
  }
};

// Track scroll depth for content engagement
export const trackScrollDepth = (percentage) => {
  if (isAnalyticsAvailable()) {
    window.gtag('event', 'scroll', {
      event_category: 'User Engagement',
      event_label: `${percentage}% scroll`,
      value: percentage
    });
  }

  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'scroll_depth',
      depth_percentage: percentage
    });
  }
};

// Initialize scroll tracking
export const initializeScrollTracking = () => {
  if (typeof window === 'undefined') return;

  let scrollMilestones = [25, 50, 75, 90, 100];
  let trackedMilestones = new Set();

  const handleScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );

    scrollMilestones.forEach(milestone => {
      if (scrollPercent >= milestone && !trackedMilestones.has(milestone)) {
        trackScrollDepth(milestone);
        trackedMilestones.add(milestone);
      }
    });
  };

  window.addEventListener('scroll', handleScroll);

  // Clean up function
  return () => window.removeEventListener('scroll', handleScroll);
};

// Module-level tracking state to prevent duplicate initializations
let engagementTrackingCleanup = null;

/**
 * Explicitly clean up engagement tracking.
 * Call this to remove all event listeners and stop polling without re-initializing.
 * This prevents memory leaks if the returned cleanup function from initializeEngagementTracking()
 * was discarded or if you want to stop tracking without starting a new session.
 */
export const cleanupEngagementTracking = () => {
  if (engagementTrackingCleanup) {
    engagementTrackingCleanup();
    engagementTrackingCleanup = null;
  }
};

/**
 * Initialize engagement tracking for the current page session.
 * Tracks user engagement milestones (30s, 60s, 120s, 300s) based on effective time
 * (excluding time when tab is hidden). Also tracks page exit events.
 *
 * @returns {Function} Cleanup function to stop tracking and remove listeners.
 *                     Alternatively, call cleanupEngagementTracking() to force cleanup.
 */
export const initializeEngagementTracking = () => {
  if (typeof window === 'undefined') return;

  // Clean up any existing engagement tracking before re-initializing
  cleanupEngagementTracking();

  let startTime = Date.now();
  let pausedTime = 0; // Time accumulated while tab was hidden
  // Use -1 as sentinel instead of null to avoid edge case where Date.now() = 0 would be falsy
  let pauseStart = -1; // When the tab became hidden (-1 = not paused)
  const engagementMilestones = [30, 60, 120, 300]; // seconds
  const trackedEngagement = new Set();
  let intervalId = null;
  let isActive = true;
  let exitTracked = false; // Flag to prevent duplicate page_exit events (pagehide + beforeunload both fire on mobile)

  const checkEngagement = () => {
    // Don't check if tab is hidden or tracking is stopped
    if (!isActive || document.visibilityState === 'hidden') return;

    // Calculate effective time (excluding time when tab was hidden)
    const effectiveTime = Math.round((Date.now() - startTime - pausedTime) / 1000);

    let allMilestonesTracked = true;
    engagementMilestones.forEach(milestone => {
      if (!trackedEngagement.has(milestone)) {
        if (effectiveTime >= milestone) {
          trackEngagementMilestone(`${milestone}s_engaged`, effectiveTime);
          trackedEngagement.add(milestone);
        } else {
          allMilestonesTracked = false;
        }
      }
    });

    // Stop polling once all milestones are tracked
    if (allMilestonesTracked && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  // Handle visibility changes to pause/resume tracking
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      // Tab became hidden - record pause start time
      pauseStart = Date.now();
    } else if (document.visibilityState === 'visible' && pauseStart !== -1) {
      // Tab became visible again - add paused duration
      pausedTime += Date.now() - pauseStart;
      pauseStart = -1;
      // Check immediately when tab becomes visible
      checkEngagement();
    }
  };

  // Track when user leaves page (guard prevents duplicate events when both pagehide + beforeunload fire)
  const handlePageExit = () => {
    if (exitTracked) return;
    exitTracked = true;
    const effectiveTime = Math.round((Date.now() - startTime - pausedTime) / 1000);
    trackEngagementMilestone('page_exit', effectiveTime);
  };

  // Start polling - but with a reasonable interval and auto-stop
  intervalId = setInterval(checkEngagement, 10000);

  // Add event listeners
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('beforeunload', handlePageExit);
  window.addEventListener('pagehide', handlePageExit);

  // Clean up function
  const cleanup = () => {
    isActive = false;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', handlePageExit);
    window.removeEventListener('pagehide', handlePageExit);
  };

  // Store cleanup reference for future re-initialization
  engagementTrackingCleanup = cleanup;

  return cleanup;
};

// --- Field Web Vitals reporting (LCP, CLS, INP, FID) ---
let webVitalsInitialized = false;

export const initializeWebVitals = (options = {}) => {
  if (typeof window === 'undefined' || webVitalsInitialized) return;
  // Optionally restrict to mobile sessions (pointer: coarse)
  if (options.mobileOnly) {
    try {
      if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) {
        return;
      }
    } catch { /* no-op */ }
  }

  const sendMetric = (name, value, id) => {
    if (isAnalyticsAvailable()) {
      window.gtag('event', name.toLowerCase(), {
        value,
        metric_id: id,
        metric_name: name,
        event_category: 'Web Vitals',
        non_interaction: true
      });
    }
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'web_vitals',
        metric_name: name,
        metric_value: value,
        metric_id: id
      });
    }
  };

  let clsValue = 0;
  let inpValue = 0;

  try {
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          // LCP in ms
          sendMetric('LCP', Math.round(entry.startTime), entry.id || 'lcp');
        } else if (entry.entryType === 'layout-shift' && !entry.hadRecentInput) {
          // CLS is cumulative
          clsValue += entry.value;
          sendMetric('CLS', Number(clsValue.toFixed(3)), entry.id || 'cls');
        } else if (entry.entryType === 'first-input') {
          // FID in ms
          const fid = entry.processingStart - entry.startTime;
          if (fid >= 0) sendMetric('FID', Math.round(fid), entry.name || 'fid');
        } else if (entry.entryType === 'event' && (entry.name === 'click' || entry.name === 'keydown' || entry.name === 'pointerdown')) {
          // INP best-effort: track max observed interaction latency
          const latency = entry.duration || (entry.processingEnd - entry.startTime);
          if (latency > inpValue) {
            inpValue = latency;
            sendMetric('INP', Math.round(inpValue), entry.name || 'inp');
          }
        }
      }
    });

    try { po.observe({ type: 'largest-contentful-paint', buffered: true }); } catch {
      // Observer might not support this type
    }
    try { po.observe({ type: 'layout-shift', buffered: true }); } catch {
      // Observer might not support this type
    }
    try { po.observe({ type: 'first-input', buffered: true }); } catch {
      // Observer might not support this type
    }
    try { po.observe({ type: 'event', durationThreshold: 40, buffered: true }); } catch {
      // Observer might not support this type
    }

    const finalize = () => {
      try { po.disconnect(); } catch {
        // Disconnect might fail if already disconnected
      }
      sendMetric('CLS', Number(clsValue.toFixed(3)), 'cls-final');
      if (inpValue > 0) sendMetric('INP', Math.round(inpValue), 'inp-final');
    };

    // Named handler for visibilitychange to enable cleanup
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') finalize();
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', finalize);

    // Cleanup function to remove event listeners
    const cleanup = () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', finalize);
    };

    webVitalsInitialized = true;
    return cleanup;
  } catch {
    // PerformanceObserver not supported - mark as initialized to prevent retry
    webVitalsInitialized = true;
  }
};